<script setup>
import { pageTransition } from '~/helpers/animations/animations';

definePageMeta({
  pageTransition: pageTransition,
});

const { t } = useI18n();

const route = useRoute();

const pageDataQuery = gql`
  query GetPageData($routeId: String!) {
    page(route_id: $routeId) {
      breadcrumbs {
        id
        name
        route_id
        slug: seo_url_slug {
          cs
          en
        }
      }
      seo_meta_title
      seo_meta_description
      seo_meta_keywords
      name
      content
      children {
        id
        name
        content
        image
        perex
        route_id
        slug: seo_url_slug {
          cs
          en
        }
      }
      documents {
        name
        type
        file {
          url
        }
      }
      contacts {
        id
        name
        job
        slug: seo_url_slug {
          cs
          en
        }
        image
        group {
          name
        }
        building {
          id
          name
          seo_url_slug {
            cs
            en
          }
        }
      }
      events {
        id
        name
        type
        slug: seo_url_slug {
          cs
          en
        }
        image: image_list {
          url
          width
          height
        }
        imageCrop: image_list_crop_to
        buildings {
          id
          name
          seo_url_slug {
            cs
            en
          }
        }
        dates {
          id
          start_at {
            timestamp
          }
          end_at {
            timestamp
          }
        }
      }
    }
  }
`;

const { data } = await useAsyncQuery(pageDataQuery, { routeId: route.name });
const page = ref(data.value?.page);

const breadcrumbs = useBreadCrumbs(data.value?.page.breadcrumbs || []);

useHeadData({
  title: page.value?.seo_meta_title || page.value?.name || null,
  description: page.value?.seo_meta_description || null,
  keywords: page.value?.seo_meta_keywords || null,
  image: page.value?.image || page.value?.cropped_detail_image || null,
  url: route.fullPath,
});
</script>

<template>
  <main v-if="page" class="main">
    <Breadcrumbs :items="breadcrumbs" />
    <div v-if="page" class="container-max-content">
      <h1 class="heading">{{ page.name }}</h1>
      <DynamicPageContent v-if="page.content" :content="page.content" class="mt-5" />

      <Accordion class="mt-5" v-if="page.children.length">
        <AccordionItem :title="page.name" v-for="(page, key) in page.children" :key="key" :index="key">
          <div>
            <DynamicPageContent v-if="page.content" :content="page.content" />
          </div>
        </AccordionItem>
      </Accordion>
      <ContactList v-if="page.contacts.length" :contacts="page.contacts" />
      <DocumentList v-if="page.documents.length" :documents="page.documents" />

      <div v-if="page.events.length" class="mt-5">
        <h2 class="heading heading--2 heading--thin">
          {{ t('pages.news.detail.events') }}
        </h2>

        <EventGrid :data="page.events" class="mt-4" hide-type />
      </div>
    </div>
  </main>
</template>
